import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { CalendarRange, Filter, X } from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints, shadows } from '../../styles/theme';
import {
  EMPTY_FILTERS,
  FORMAT_OPTIONS,
  RELATIONSHIP_OPTIONS,
  TIME_OF_DAY_OPTIONS,
  UPCOMING_HORIZON_OPTIONS,
  countActiveFilters,
  toggleFilterValue,
  type EventFilterState,
  type RelationshipFilter,
  type TimeOfDayFilter,
  type UpcomingHorizonDays,
} from '../../utils/eventFilters';
import type { EventFormat } from '../../utils/eventPresentation';

/**
 * Compact filter toolbar on desktop; a full-height drawer on mobile. Both
 * render the same controls and write to the same URL-backed state, so the map,
 * the list, and the exports always agree.
 */

const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing[2]};
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
  }
`;

const HorizonField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 44px;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};

  select {
    min-height: 44px;
    padding: 0 ${spacing[8]} 0 ${spacing[3]};
    border: 1px solid ${colors.border.normal};
    border-radius: ${borderRadius.md};
    background: ${colors.white};
    color: ${colors.text.primary};
    font: inherit;

    &:focus-visible {
      outline: 2px solid ${colors.border.focus};
      outline-offset: 2px;
    }
  }
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 44px;
  padding: 0 ${spacing[4]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary[400]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: ${borderRadius.full};
  background: ${colors.primary[500]};
  color: ${colors.white};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  min-height: 44px;
  padding: 0 ${spacing[3]};
  border: none;
  background: none;
  color: ${colors.text.link};
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const Panel = styled.div`
  margin-top: ${spacing[3]};
  padding: ${spacing[4]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.white};
  box-shadow: ${shadows.sm};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing[4]};

  @media (max-width: ${breakpoints.md}) {
    position: fixed;
    inset: 0;
    z-index: 1400;
    margin: 0;
    border: none;
    border-radius: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: block;
    padding: 0 ${spacing[4]};
  }
`;

const DrawerHeader = styled.div`
  display: none;

  @media (max-width: ${breakpoints.md}) {
    display: flex;
    position: sticky;
    top: 0;
    z-index: 1;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${spacing[4]};
    padding: ${spacing[3]} 0;
    border-bottom: 1px solid ${colors.border.light};
    background: ${colors.white};
  }
`;

const DrawerTitle = styled.h2`
  margin: 0;
  font-size: ${typography.fontSizes.lg};
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.background.secondary};
  color: ${colors.text.secondary};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const Group = styled.fieldset`
  border: none;
  margin: 0 0 ${spacing[4]} 0;
  padding: 0;
  min-width: 0;
`;

const GroupTitle = styled.legend`
  padding: 0;
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
`;

const OptionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const OptionLabel = styled.label.withConfig({
  shouldForwardProp: (prop) => prop !== 'isChecked',
})<{ isChecked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 44px;
  padding: 0 ${spacing[3]};
  border: 1px solid ${(props) => (props.isChecked ? colors.primary[400] : colors.border.normal)};
  border-radius: ${borderRadius.md};
  background: ${(props) => (props.isChecked ? colors.primary[50] : colors.white)};
  color: ${(props) => (props.isChecked ? colors.primary[800] : colors.text.secondary)};
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary[300]};
  }

  &:focus-within {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(128px, 1fr));
  gap: ${spacing[2]};

  @media (max-width: ${breakpoints.md}) {
    order: 3;
    flex: 1 0 100%;
    width: 100%;
  }
`;

const DateField = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: 0;
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};

  input {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 44px;
    padding: 0 ${spacing[2]};
    border: 1px solid ${colors.border.normal};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSizes.sm};
    color: ${colors.text.primary};

    &:focus-visible {
      outline: 2px solid ${colors.border.focus};
      outline-offset: 1px;
    }
  }
`;

const DrawerFooter = styled.div`
  display: none;

  @media (max-width: ${breakpoints.md}) {
    display: flex;
    position: sticky;
    bottom: 0;
    z-index: 1;
    flex-direction: column;
    gap: ${spacing[2]};
    margin: ${spacing[4]} -${spacing[4]} 0;
    padding: ${spacing[3]} ${spacing[4]} ${spacing[4]};
    border-top: 1px solid ${colors.border.light};
    background: ${colors.white};
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const DrawerResultStatus = styled.span`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  text-align: center;
`;

const ViewResultsButton = styled.button`
  width: 100%;
  min-height: 48px;
  padding: 0 ${spacing[4]};
  border: 1px solid ${colors.primary[700]};
  border-radius: ${borderRadius.md};
  background: ${colors.primary[600]};
  color: ${colors.white};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;

  &:hover {
    background: ${colors.primary[700]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const EntityTools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};
`;

const EntitySearch = styled.input`
  width: 100%;
  min-height: 44px;
  margin-bottom: ${spacing[2]};
  padding: 0 ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 1px;
  }
`;

const EntityAction = styled.button`
  min-height: 36px;
  padding: 0 ${spacing[2]};
  border: 0;
  background: none;
  color: ${colors.text.link};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
  }
`;

const EntityOptions = styled(OptionList)`
  max-height: 260px;
  overflow-y: auto;
`;

interface CheckboxGroupProps<T extends string> {
  title: string;
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onToggle: (value: T) => void;
}

function CheckboxGroup<T extends string>({ title, options, selected, onToggle }: CheckboxGroupProps<T>) {
  if (options.length === 0) return null;
  return (
    <Group>
      <GroupTitle>{title}</GroupTitle>
      <OptionList>
        {options.map((option) => {
          const isChecked = selected.includes(option.value);
          return (
            <OptionLabel key={option.value} isChecked={isChecked}>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggle(option.value)}
              />
              {option.label}
            </OptionLabel>
          );
        })}
      </OptionList>
    </Group>
  );
}

export interface EventFilterBarProps {
  filters: EventFilterState;
  onChange: (filters: EventFilterState) => void;
  /** Vibes present in the current result set. */
  vibeOptions: string[];
  /** Rendered next to the controls, e.g. "18 of 42 events". */
  resultSummary?: React.ReactNode;
  entityOptions?: Array<{ value: number; label: string }>;
  isLoading?: boolean;
  resultCount?: number;
}

export const EventFilterBar: React.FC<EventFilterBarProps> = ({
  filters,
  onChange,
  vibeOptions,
  resultSummary,
  entityOptions = [],
  isLoading = false,
  resultCount = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const activeCount = countActiveFilters(filters);
  const [entitySearch, setEntitySearch] = useState('');

  // Escape closes the mobile drawer and returns focus to the trigger.
  useEffect(() => {
    if (!isOpen) return;
    const isMobile = window.matchMedia?.(`(max-width: ${breakpoints.md})`).matches ?? false;
    const previousOverflow = document.body.style.overflow;
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      closeRef.current?.focus();
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (isMobile) document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const update = (patch: Partial<EventFilterState>) => onChange({ ...filters, ...patch });
  const closeFilters = () => {
    setIsOpen(false);
    toggleRef.current?.focus();
  };
  const resultLabel = isLoading
    ? 'Updating results…'
    : `${resultCount} matching event${resultCount === 1 ? '' : 's'}`;

  return (
    <div>
      <Toolbar>
        <HorizonField>
          <CalendarRange aria-hidden="true" size={16} />
          Show
          <select
            aria-label="Upcoming event range"
            value={filters.dateTo ? 'custom' : filters.horizonDays}
            onChange={(event) => update({
              horizonDays: Number(event.target.value) as UpcomingHorizonDays,
              dateTo: '',
            })}
          >
            {filters.dateTo && <option value="custom">Custom end date</option>}
            {UPCOMING_HORIZON_OPTIONS.map((days) => (
              <option key={days} value={days}>{`Next ${days} days`}</option>
            ))}
          </select>
        </HorizonField>
        <DateRow role="group" aria-label="Date range">
          <DateField>
            From
            <input
              type="date"
              value={filters.dateFrom}
              max={filters.dateTo || undefined}
              onChange={(event) => update({ dateFrom: event.target.value })}
            />
          </DateField>
          <DateField>
            To
            <input
              type="date"
              value={filters.dateTo}
              min={filters.dateFrom || undefined}
              onChange={(event) => update({ dateTo: event.target.value })}
            />
          </DateField>
        </DateRow>
        <ToggleButton
          ref={toggleRef}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-controls={panelId}
          aria-haspopup="dialog"
        >
          <Filter aria-hidden="true" size={16} />
          Filters
          {activeCount > 0 && <CountPill aria-label={`${activeCount} active filters`}>{activeCount}</CountPill>}
        </ToggleButton>
        {activeCount > 0 && (
          <ClearButton type="button" onClick={() => onChange(EMPTY_FILTERS)}>
            <X aria-hidden="true" size={14} />
            Clear all
          </ClearButton>
        )}
        {resultSummary}
      </Toolbar>

      {isOpen && (
        <Panel id={panelId} role="dialog" aria-label="Event filters">
          <DrawerHeader>
            <DrawerTitle>Filters</DrawerTitle>
            <CloseButton
              ref={closeRef}
              type="button"
              onClick={closeFilters}
              aria-label="Close filters and view results"
              title="Close filters"
            >
              <X aria-hidden="true" size={20} />
            </CloseButton>
          </DrawerHeader>

          <CheckboxGroup
            title="Format"
            options={FORMAT_OPTIONS}
            selected={filters.formats}
            onToggle={(value: EventFormat) => update({ formats: toggleFilterValue(filters.formats, value) })}
          />

          <CheckboxGroup
            title="Relationship"
            options={RELATIONSHIP_OPTIONS}
            selected={filters.relationships}
            onToggle={(value: RelationshipFilter) =>
              update({ relationships: toggleFilterValue(filters.relationships, value) })
            }
          />

          <CheckboxGroup
            title="Time of day"
            options={TIME_OF_DAY_OPTIONS.map((value) => ({ value, label: value }))}
            selected={filters.timesOfDay}
            onToggle={(value: TimeOfDayFilter) =>
              update({ timesOfDay: toggleFilterValue(filters.timesOfDay, value) })
            }
          />

          <CheckboxGroup
            title="Vibe"
            options={vibeOptions.map((value) => ({ value, label: value }))}
            selected={filters.vibes}
            onToggle={(value: string) => update({ vibes: toggleFilterValue(filters.vibes, value) })}
          />

          {entityOptions.length > 0 && (
            <Group>
              <GroupTitle>Entities</GroupTitle>
              {entityOptions.length > 8 && (
                <EntitySearch
                  type="search"
                  value={entitySearch}
                  placeholder="Find an entity"
                  aria-label="Find an entity filter"
                  onChange={(event) => setEntitySearch(event.target.value)}
                />
              )}
              <EntityTools>
                <EntityAction type="button" onClick={() => update({ hiddenEntityIds: [] })}>Show all</EntityAction>
                <EntityAction
                  type="button"
                  onClick={() => update({ hiddenEntityIds: entityOptions.map((option) => option.value) })}
                >
                  Hide all
                </EntityAction>
              </EntityTools>
              <EntityOptions>
                {entityOptions
                  .filter((option) => option.label.toLowerCase().includes(entitySearch.trim().toLowerCase()))
                  .map((option) => {
                    const isChecked = !filters.hiddenEntityIds.includes(option.value);
                    return (
                      <OptionLabel key={option.value} isChecked={isChecked}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => update({
                            hiddenEntityIds: isChecked
                              ? [...filters.hiddenEntityIds, option.value]
                              : filters.hiddenEntityIds.filter((id) => id !== option.value),
                          })}
                        />
                        {option.label}
                      </OptionLabel>
                    );
                  })}
              </EntityOptions>
            </Group>
          )}

          <DrawerFooter>
            <DrawerResultStatus role="status" aria-live="polite">{resultLabel}</DrawerResultStatus>
            <ViewResultsButton type="button" onClick={closeFilters}>
              {isLoading ? 'View results' : `View ${resultCount} result${resultCount === 1 ? '' : 's'}`}
            </ViewResultsButton>
          </DrawerFooter>
        </Panel>
      )}
    </div>
  );
};

export default EventFilterBar;
